import React, { useEffect, useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import {
  Box,
  ButtonGroup,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Popover,
  Chip,
} from '@mui/material';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Code,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Undo,
  Redo,
  Eraser,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  Unlink,
  Image as ImageIcon,
  Paperclip,
  Upload,
  Globe,
  Smile,
} from 'lucide-react';
import api from '../services/api';

export interface TiptapEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  label?: string;
  editable?: boolean;
  uploadEndpoint?: string;
  enableMedia?: boolean;
}

const EMOJI_GROUPS = [
  {
    category: 'การศึกษา & โรงเรียน',
    emojis: [
      '🎓', '🏫', '🏛️', '📖', '📚', '📝', '✏️', '🖊️', '🖋️', '📐', '📏',
      '🔬', '🧪', '💻', '🖥️', '🖨️', '📁', '📂', '📑', '📊', '📈', '📉',
      '📋', '📌', '📍', '🗺️', '🚌', '🚗', '⏱️', '⌛', '🏆', '🥇', '🥈',
      '🥉', '🏅', '🎖️', '📜', '🧾', '🧑‍🏫', '👩‍🏫', '👨‍🎓', '👩‍🎓', '🎒', '💼',
      '🤝', '🙌', '🙋‍♂️', '🙋‍♀️', '🧑‍💻', '👩‍💻', '🎯', '✨'
    ],
  },
  {
    category: 'สัญลักษณ์ & ประกาศ',
    emojis: [
      '✅', '✔️', '☑️', '❌', '❎', '⚠️', '❗', '❕', '❓', '❔', 'ℹ️',
      '📢', '📣', '🔔', '🔕', '💡', '🔑', '🔒', '🔓', '🛡️', '🏷️', '🔖',
      '📌', '📍', '🚩', '🏁', '⭐', '🌟', '💫', '🔥', '💥', '🎉', '🎊',
      '💬', '🗨️', '💭', '📞', '📱', '✉️', '📧', '🌐', '🔗', '📅', '📆',
      '🕒', '⏰', '💯', '🆕', '🆙', '🆗', '🔝', '🆓', '🔴', '🟢', '🟡', '🔵'
    ],
  },
  {
    category: 'อารมณ์ & ท่าทาง',
    emojis: [
      '😊', '😃', '😄', '😁', '🥰', '😍', '🤩', '😎', '🥳', '🤗', '👍',
      '👏', '🙌', '🙏', '💪', '✌️', '👌', '👋', '✍️', '❤️', '💙', '💚',
      '💛', '🧡', '💜', '🤍', '🤎', '💖', '💐', '🌸', '🌻', '☀️', '🌈'
    ],
  },
  {
    category: 'ลูกศร & สัญลักษณ์',
    emojis: [
      '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '↩️',
      '↪️', '🔄', '🔁', '▶️', '◀️', '⏩', '⏪', '🔼', '🔽', '➕', '➖',
      '✖️', '➗', '🟰', '🔹', '🔸', '🔺', '🔻', '💠', '🔘', '▪️', '▫️'
    ],
  },
];

export const TiptapEditor: React.FC<TiptapEditorProps> = ({
  value,
  onChange,
  placeholder = 'พิมพ์รายละเอียดเนื้อหาที่นี่...',
  minHeight = 160,
  label,
  editable = true,
  uploadEndpoint = '/announcements/upload-media',
  enableMedia = true,
}) => {
  // Dialog / Menu states
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  const [imageUrlDialogOpen, setImageUrlDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');

  const [imageMenuAnchor, setImageMenuAnchor] = useState<null | HTMLElement>(null);
  const [uploading, setUploading] = useState(false);

  // Emoji Picker states
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<null | HTMLElement>(null);
  const [emojiCategory, setEmojiCategory] = useState(0);
  const [emojiSearch, setEmojiSearch] = useState('');

  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const docFileInputRef = useRef<HTMLInputElement>(null);

  const normalizeUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('/uploads/')) return `/api/v1${url}`;
    return url;
  };

  const normalizeHtmlContent = (html?: string) => {
    if (!html) return '';
    return html.replace(/src="\/uploads\//g, 'src="/api/v1/uploads/').replace(/href="\/uploads\//g, 'href="/api/v1/uploads/');
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'tiptap-link',
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: normalizeHtmlContent(value),
    editable,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        style: `min-height: ${minHeight}px; outline: none; padding: 14px 16px; font-family: 'Sarabun', sans-serif; font-size: 0.95rem; line-height: 1.65;`,
        'data-placeholder': placeholder,
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(normalizeHtmlContent(value), { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  // Handle Link Dialog Open
  const handleOpenLinkDialog = () => {
    const previousUrl = editor.getAttributes('link').href;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    setLinkUrl(previousUrl || '');
    setLinkText(selectedText || '');
    setLinkDialogOpen(true);
  };

  const handleApplyLink = () => {
    if (!linkUrl) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setLinkDialogOpen(false);
      return;
    }

    let formattedUrl = linkUrl.trim();
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://') && !formattedUrl.startsWith('/') && !formattedUrl.startsWith('mailto:')) {
      formattedUrl = `https://${formattedUrl}`;
    }

    if (linkText && editor.state.selection.empty) {
      editor.chain().focus().insertContent(`<a href="${formattedUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a> `).run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: formattedUrl }).run();
    }

    setLinkDialogOpen(false);
    setLinkUrl('');
    setLinkText('');
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setLinkDialogOpen(false);
  };

  // Handle Image Upload from Device
  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageMenuAnchor(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(uploadEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data?.url) {
        const finalUrl = normalizeUrl(res.data.url);
        editor.chain().focus().setImage({ src: finalUrl, alt: file.name }).run();
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
    } finally {
      setUploading(false);
      if (imageFileInputRef.current) {
        imageFileInputRef.current.value = '';
      }
    }
  };

  // Handle Image from URL
  const handleApplyImageUrl = () => {
    if (imageUrl) {
      let finalUrl = normalizeUrl(imageUrl.trim());
      editor.chain().focus().setImage({ src: finalUrl, alt: imageAlt || undefined }).run();
    }
    setImageUrlDialogOpen(false);
    setImageUrl('');
    setImageAlt('');
  };

  // Handle Document Attachment Upload
  const handleDocFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(uploadEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data?.url) {
        const finalUrl = normalizeUrl(res.data.url);
        const sizeFormatted = file.size > 1024 * 1024
          ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
          : `${Math.round(file.size / 1024)} KB`;
        
        // Insert inline attachment link
        const linkHtml = `<a href="${finalUrl}" target="_blank" rel="noopener noreferrer" style="color:#1E40AF; font-weight:600; text-decoration:underline;">📎 ${file.name} (${sizeFormatted})</a> `;
        editor.chain().focus().insertContent(linkHtml).run();
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'เกิดข้อผิดพลาดในการอัปโหลดไฟล์เอกสาร');
    } finally {
      setUploading(false);
      if (docFileInputRef.current) {
        docFileInputRef.current.value = '';
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            color: '#475569',
            mb: 0.8,
            fontSize: '0.85rem',
            fontFamily: '"Prompt", sans-serif',
          }}
        >
          {label}
        </Typography>
      )}

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={imageFileInputRef}
        onChange={handleImageFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={docFileInputRef}
        onChange={handleDocFileChange}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
        style={{ display: 'none' }}
      />

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          overflow: 'hidden',
          borderColor: 'rgba(203, 213, 225, 0.9)',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:focus-within': {
            borderColor: '#1E40AF',
            boxShadow: '0 0 0 2px rgba(30, 64, 175, 0.15)',
          },
        }}
      >
        {/* Toolbar */}
        {editable && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              p: 0.8,
              bgcolor: '#F8FAFC',
              borderBottom: '1px solid #E2E8F0',
            }}
          >
            {/* Bold, Italic, Underline, Strike, Code */}
            <ButtonGroup size="small" variant="text">
              <Tooltip title="ตัวหนา (Bold)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  color={editor.isActive('bold') ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('bold') ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('bold') ? '#1E40AF' : '#475569',
                  }}
                >
                  <Bold size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="ตัวเอียง (Italic)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  color={editor.isActive('italic') ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('italic') ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('italic') ? '#1E40AF' : '#475569',
                  }}
                >
                  <Italic size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="ขีดเส้นใต้ (Underline)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  color={editor.isActive('underline') ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('underline') ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('underline') ? '#1E40AF' : '#475569',
                  }}
                >
                  <UnderlineIcon size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="ขีดฆ่า (Strikethrough)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  color={editor.isActive('strike') ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('strike') ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('strike') ? '#1E40AF' : '#475569',
                  }}
                >
                  <Strikethrough size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="โค้ด (Code)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleCode().run()}
                  color={editor.isActive('code') ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('code') ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('code') ? '#1E40AF' : '#475569',
                  }}
                >
                  <Code size={16} />
                </IconButton>
              </Tooltip>
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

            {/* Headings */}
            <ButtonGroup size="small" variant="text">
              <Tooltip title="หัวข้อขนาดใหญ่ (Heading 2)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('heading', { level: 2 }) ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('heading', { level: 2 }) ? '#1E40AF' : '#475569',
                  }}
                >
                  <Heading2 size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="หัวข้อย่อย (Heading 3)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('heading', { level: 3 }) ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('heading', { level: 3 }) ? '#1E40AF' : '#475569',
                  }}
                >
                  <Heading3 size={16} />
                </IconButton>
              </Tooltip>
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

            {/* Alignments */}
            <ButtonGroup size="small" variant="text">
              <Tooltip title="ชิดซ้าย (Align Left)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive({ textAlign: 'left' }) ? '#EFF6FF' : 'transparent',
                    color: editor.isActive({ textAlign: 'left' }) ? '#1E40AF' : '#475569',
                  }}
                >
                  <AlignLeft size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="กึ่งกลาง (Align Center)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive({ textAlign: 'center' }) ? '#EFF6FF' : 'transparent',
                    color: editor.isActive({ textAlign: 'center' }) ? '#1E40AF' : '#475569',
                  }}
                >
                  <AlignCenter size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="ชิดขวา (Align Right)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive({ textAlign: 'right' }) ? '#EFF6FF' : 'transparent',
                    color: editor.isActive({ textAlign: 'right' }) ? '#1E40AF' : '#475569',
                  }}
                >
                  <AlignRight size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="จัดเต็มบรรทัด (Align Justify)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                  color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive({ textAlign: 'justify' }) ? '#EFF6FF' : 'transparent',
                    color: editor.isActive({ textAlign: 'justify' }) ? '#1E40AF' : '#475569',
                  }}
                >
                  <AlignJustify size={16} />
                </IconButton>
              </Tooltip>
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

            {/* Lists, Quote, Divider */}
            <ButtonGroup size="small" variant="text">
              <Tooltip title="รายการแบบจุด (Bullet List)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  color={editor.isActive('bulletList') ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('bulletList') ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('bulletList') ? '#1E40AF' : '#475569',
                  }}
                >
                  <List size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="รายการแบบตัวเลข (Ordered List)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  color={editor.isActive('orderedList') ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('orderedList') ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('orderedList') ? '#1E40AF' : '#475569',
                  }}
                >
                  <ListOrdered size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="ข้อความอ้างอิง (Blockquote)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  color={editor.isActive('blockquote') ? 'primary' : 'default'}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: editor.isActive('blockquote') ? '#EFF6FF' : 'transparent',
                    color: editor.isActive('blockquote') ? '#1E40AF' : '#475569',
                  }}
                >
                  <Quote size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="เส้นคั่นแนวนอน (Divider Line)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().setHorizontalRule().run()}
                  sx={{ borderRadius: 1.5, color: '#475569' }}
                >
                  <Minus size={16} />
                </IconButton>
              </Tooltip>
            </ButtonGroup>

            {/* Media & Link Tools */}
            {enableMedia && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

                <ButtonGroup size="small" variant="text">
                  <Tooltip title="แทรกลิงก์ (Link)">
                    <IconButton
                      size="small"
                      onClick={handleOpenLinkDialog}
                      color={editor.isActive('link') ? 'primary' : 'default'}
                      sx={{
                        borderRadius: 1.5,
                        bgcolor: editor.isActive('link') ? '#EFF6FF' : 'transparent',
                        color: editor.isActive('link') ? '#1E40AF' : '#475569',
                      }}
                    >
                      <LinkIcon size={16} />
                    </IconButton>
                  </Tooltip>
                  {editor.isActive('link') && (
                    <Tooltip title="ยกเลิกลิงก์ (Unlink)">
                      <IconButton
                        size="small"
                        onClick={handleRemoveLink}
                        sx={{ borderRadius: 1.5, color: '#DC2626' }}
                      >
                        <Unlink size={16} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="แทรกรูปภาพ (Insert Image)">
                    <IconButton
                      size="small"
                      onClick={(e) => setImageMenuAnchor(e.currentTarget)}
                      sx={{
                        borderRadius: 1.5,
                        color: '#1E40AF',
                        bgcolor: imageMenuAnchor ? '#EFF6FF' : 'transparent',
                      }}
                      disabled={uploading}
                    >
                      {uploading ? <CircularProgress size={16} /> : <ImageIcon size={16} />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="แนบไฟล์เอกสารในข้อความ (Attach File Link)">
                    <IconButton
                      size="small"
                      onClick={() => docFileInputRef.current?.click()}
                      sx={{
                        borderRadius: 1.5,
                        color: '#059669',
                      }}
                      disabled={uploading}
                    >
                      <Paperclip size={16} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="แทรกไอคอน / อีโมจิ (Insert Icon & Emoji)">
                    <IconButton
                      size="small"
                      onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                      sx={{
                        borderRadius: 1.5,
                        color: '#D97706',
                        bgcolor: emojiAnchorEl ? '#FEF3C7' : 'transparent',
                      }}
                    >
                      <Smile size={16} />
                    </IconButton>
                  </Tooltip>
                </ButtonGroup>
              </>
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

            {/* Clear, Undo, Redo */}
            <ButtonGroup size="small" variant="text">
              <Tooltip title="ล้างการจัดรูปแบบ (Clear Formatting)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
                  sx={{ borderRadius: 1.5, color: '#64748B' }}
                >
                  <Eraser size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="เลิกทำ (Undo)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().undo().run()}
                  disabled={!editor.can().undo()}
                  sx={{ borderRadius: 1.5, color: '#64748B' }}
                >
                  <Undo size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="ทำซ้ำ (Redo)">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().redo().run()}
                  disabled={!editor.can().redo()}
                  sx={{ borderRadius: 1.5, color: '#64748B' }}
                >
                  <Redo size={16} />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
          </Box>
        )}

        {/* Content Area */}
        <Box
          sx={{
            minHeight,
            cursor: editable ? 'text' : 'default',
            bgcolor: '#FFFFFF',
            '& .ProseMirror': {
              minHeight: `${minHeight}px`,
              outline: 'none',
              padding: '14px 16px',
              fontFamily: "'Sarabun', sans-serif",
              fontSize: '0.95rem',
              lineHeight: 1.65,
              color: '#1E293B',
              '& h2': {
                fontFamily: "'Prompt', sans-serif",
                fontSize: '1.35rem',
                fontWeight: 700,
                color: '#1E3A8A',
                mt: 1.5,
                mb: 1,
              },
              '& h3': {
                fontFamily: "'Prompt', sans-serif",
                fontSize: '1.15rem',
                fontWeight: 600,
                color: '#1E40AF',
                mt: 1.2,
                mb: 0.8,
              },
              '& p': {
                margin: '0.4em 0',
              },
              '& ul, & ol': {
                paddingLeft: '1.6em',
                margin: '0.4em 0',
              },
              '& blockquote': {
                borderLeft: '4px solid #3B82F6',
                margin: '0.8em 0',
                paddingLeft: '1em',
                color: '#475569',
                fontStyle: 'italic',
                bgcolor: '#F8FAFC',
                py: 0.5,
                borderRadius: '0 6px 6px 0',
              },
              '& code': {
                bgcolor: '#F1F5F9',
                color: '#DC2626',
                padding: '2px 6px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.9em',
              },
              '& hr': {
                border: 'none',
                borderTop: '1px solid #E2E8F0',
                margin: '1.2em 0',
              },
              '& u': {
                textDecoration: 'underline',
              },
              '& a, & .tiptap-link': {
                color: '#1E40AF',
                textDecoration: 'underline',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  color: '#2563EB',
                },
              },
              '& img, & .tiptap-image': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                margin: '10px 0',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                display: 'block',
              },
            },
          }}
          onClick={() => {
            if (editable && !editor.isFocused) {
              editor.commands.focus();
            }
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Paper>

      {/* Image Insertion Menu */}
      <Menu
        anchorEl={imageMenuAnchor}
        open={Boolean(imageMenuAnchor)}
        onClose={() => setImageMenuAnchor(null)}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 200, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' } } }}
      >
        <MenuItem
          onClick={() => {
            setImageMenuAnchor(null);
            imageFileInputRef.current?.click();
          }}
        >
          <ListItemIcon>
            <Upload size={18} color="#1E40AF" />
          </ListItemIcon>
          <ListItemText primary="อัปโหลดภาพจากอุปกรณ์" secondary="PNG, JPG, WebP" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImageMenuAnchor(null);
            setImageUrlDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <Globe size={18} color="#059669" />
          </ListItemIcon>
          <ListItemText primary="ระบุ URL ลิงก์รูปภาพ" secondary="https://..." />
        </MenuItem>
      </Menu>

      {/* Link Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle sx={{ fontFamily: "'Prompt', sans-serif", fontWeight: 700 }}>
          {editor.isActive('link') ? 'แก้ไขลิงก์ (Edit Link)' : 'แทรกลิงก์ (Insert Link)'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '10px !important' }}>
          <TextField
            label="URL ปลายทาง"
            placeholder="https://example.com หรือ /announcements"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          {editor.state.selection.empty && (
            <TextField
              label="ข้อความที่จะแสดง (Display Text)"
              placeholder="คลิกที่นี่..."
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              fullWidth
              size="small"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          {editor.isActive('link') && (
            <Button onClick={handleRemoveLink} color="error" sx={{ mr: 'auto', fontWeight: 600 }}>
              ยกเลิกลิงก์
            </Button>
          )}
          <Button onClick={() => setLinkDialogOpen(false)} sx={{ fontWeight: 600, color: '#64748B' }}>
            ยกเลิก
          </Button>
          <Button onClick={handleApplyLink} variant="contained" sx={{ fontWeight: 700, borderRadius: 2 }}>
            ตกลง
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image URL Dialog */}
      <Dialog
        open={imageUrlDialogOpen}
        onClose={() => setImageUrlDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle sx={{ fontFamily: "'Prompt', sans-serif", fontWeight: 700 }}>
          แทรกรูปภาพจาก URL
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '10px !important' }}>
          <TextField
            label="URL ของรูปภาพ"
            placeholder="https://example.com/banner.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label="คำอธิบายรูปภาพ (Alt Text)"
            placeholder="เช่น ภาพกิจกรรมโครงการ..."
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setImageUrlDialogOpen(false)} sx={{ fontWeight: 600, color: '#64748B' }}>
            ยกเลิก
          </Button>
          <Button onClick={handleApplyImageUrl} variant="contained" sx={{ fontWeight: 700, borderRadius: 2 }}>
            แทรกรูปภาพ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Emoji & Icon Picker Popover */}
      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={() => {
          setEmojiAnchorEl(null);
          setEmojiSearch('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              width: 330,
              borderRadius: 3,
              boxShadow: '0 12px 36px rgba(15, 23, 42, 0.2)',
              border: '1px solid #E2E8F0',
            },
          },
        }}
      >
        <Box sx={{ mb: 1.2 }}>
          <TextField
            size="small"
            placeholder="ค้นหาไอคอน / อีโมจิ..."
            value={emojiSearch}
            onChange={(e) => setEmojiSearch(e.target.value)}
            fullWidth
            slotProps={{
              input: {
                startAdornment: <Smile size={16} color="#94A3B8" style={{ marginRight: 6 }} />,
                sx: { fontSize: '0.85rem', height: 36, borderRadius: 2 },
              },
            }}
          />
        </Box>

        {/* Category Filter Chips */}
        {!emojiSearch.trim() && (
          <Box sx={{ display: 'flex', gap: 0.6, mb: 1.2, overflowX: 'auto', pb: 0.5 }}>
            {EMOJI_GROUPS.map((grp, idx) => (
              <Chip
                key={grp.category}
                label={grp.category}
                size="small"
                onClick={() => setEmojiCategory(idx)}
                color={emojiCategory === idx ? 'primary' : 'default'}
                variant={emojiCategory === idx ? 'filled' : 'outlined'}
                sx={{
                  fontSize: '0.72rem',
                  height: 24,
                  cursor: 'pointer',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>
        )}

        {/* Emoji Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
            maxHeight: 220,
            overflowY: 'auto',
            p: 0.5,
          }}
        >
          {(emojiSearch.trim()
            ? EMOJI_GROUPS.flatMap((g) => g.emojis).filter((em) => em.includes(emojiSearch.trim()))
            : EMOJI_GROUPS[emojiCategory]?.emojis || []
          ).map((emoji, idx) => (
            <Button
              key={idx}
              size="small"
              onClick={() => {
                editor?.chain().focus().insertContent(emoji).run();
              }}
              sx={{
                minWidth: 36,
                height: 36,
                p: 0,
                fontSize: '1.25rem',
                borderRadius: 1.5,
                '&:hover': {
                  bgcolor: '#EFF6FF',
                  transform: 'scale(1.2)',
                },
                transition: 'transform 0.1s ease',
              }}
            >
              {emoji}
            </Button>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export const TiptapQuestionEditor = TiptapEditor;
export default TiptapEditor;
